import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Alert,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';
import Spinner from '../components/Spinner';

const getTermEndDate = (startDate?: string, endDate?: string, durationMonths?: number) => {
  if (endDate) {
    const result = new Date(endDate);
    result.setHours(23, 59, 59, 999);
    return result;
  }

  const monthCount = Number(durationMonths ?? 0);
  if (startDate && Number.isFinite(monthCount) && monthCount > 0) {
    const result = new Date(startDate);
    result.setHours(0, 0, 0, 0);
    result.setMonth(result.getMonth() + monthCount);
    result.setHours(23, 59, 59, 999);
    return result;
  }
  return null;
};

const isTermEnded = (startDate?: string, endDate?: string, durationMonths?: number) => {
  const end = getTermEndDate(startDate, endDate, durationMonths);
  return end ? Date.now() > end.getTime() : false;
};

const SchoolWorkTermHistory: React.FC = () => {
  const navigate = useNavigate();
  const { user, isLoading: loading } = useAuth();
  const [loadingResults, setLoadingResults] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submissions, setSubmissions] = useState<any[]>([]);
  const [termNames, setTermNames] = useState<string[]>([]);
  const [selectedTerm, setSelectedTerm] = useState('');
  const [termSettings, setTermSettings] = useState<{
    activeTermName?: string;
    activeTermStartDate?: string;
    activeTermEndDate?: string;
    termDurationMonths?: number;
  } | null>(null);

  useEffect(() => {
    const fetchTermHistory = async () => {
      if (!user?._id) return;
      setLoadingResults(true);
      setError(null);
      try {
        const termQuery = selectedTerm ? `?termName=${encodeURIComponent(selectedTerm)}` : '';
        const response = await api.get(`/school-work/term-history${termQuery}`);
        const data = response.data || {};
        setSubmissions(Array.isArray(data.submissions) ? data.submissions : []);
        setTermNames(Array.isArray(data.termNames) ? data.termNames : []);
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to load term history');
      } finally {
        setLoadingResults(false);
      }
    };

    fetchTermHistory();
  }, [user?._id, selectedTerm]);

  useEffect(() => {
    const loadTermSettings = async () => {
      if (!user?._id) return;
      setSettingsLoading(true);
      try {
        const response = await api.get('/school-work/settings');
        setTermSettings(response.data || null);
      } catch {
        setTermSettings(null);
      } finally {
        setSettingsLoading(false);
      }
    };

    loadTermSettings();
  }, [user?._id]);

  const summary = useMemo(() => {
    const terms = new Set<string>();
    let eligibleCount = 0;

    submissions.forEach((submission) => {
      if (submission.termName) terms.add(submission.termName);
      if (submission.isEligibleForExam) eligibleCount += 1;
    });

    return {
      termCount: terms.size,
      submissionCount: submissions.length,
      eligibleCount
    };
  }, [submissions]);

  const isParentUser = Boolean(user?.parentName || user?.parentPhone);
  const backRoute = isParentUser ? '/parent/dashboard' : '/school-work';
  const backLabel = isParentUser ? 'Back to Dashboard' : 'Back to School Work';

  const activeTermName = termSettings?.activeTermName || '';
  const activeTermEnded = isTermEnded(termSettings?.activeTermStartDate, termSettings?.activeTermEndDate, termSettings?.termDurationMonths);
  const activeTermEndText = activeTermEnded ? 'Term has ended' : activeTermName ? `Available after ${getTermEndDate(termSettings?.activeTermStartDate, termSettings?.activeTermEndDate, termSettings?.termDurationMonths)?.toLocaleDateString()}` : 'Pending term end date configuration';
  const selectedActiveTerm = selectedTerm === activeTermName && activeTermName;
  const showPendingTermInfo = selectedActiveTerm && !activeTermEnded;
  const showEarlyHistoryInfo = !activeTermEnded && !selectedTerm && !termNames.length && !submissions.length;

  if (loading || loadingResults || settingsLoading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <Spinner />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Term History
        </Typography>
        <Button variant="outlined" onClick={() => navigate(backRoute)}>
          {backLabel}
        </Button>
      </Box>

      <Paper sx={{ p: 3, mb: 4 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="subtitle2" color="text.secondary">Selected Term</Typography>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>{selectedTerm || 'All Terms'}</Typography>
          </Box>
          <Box>
            <Typography variant="subtitle2" color="text.secondary">Total Records</Typography>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>{summary.submissionCount}</Typography>
          </Box>
          <Box>
            <Typography variant="subtitle2" color="text.secondary">Terms</Typography>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>{summary.termCount}</Typography>
          </Box>
          <Box>
            <Typography variant="subtitle2" color="text.secondary">Eligible Records</Typography>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>{summary.eligibleCount}</Typography>
          </Box>
        </Stack>
      </Paper>

      <Paper sx={{ p: 3, mb: 4 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-end">
          <FormControl fullWidth sx={{ minWidth: 220 }}>
            <InputLabel id="filter-term-label">Term</InputLabel>
            <Select
              labelId="filter-term-label"
              label="Term"
              value={selectedTerm}
              onChange={(event) => setSelectedTerm(event.target.value)}
            >
              <MenuItem value="">All Terms</MenuItem>
              {termNames.map((termName) => (
                <MenuItem key={termName} value={termName}>
                  {termName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      {showPendingTermInfo && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Term history for the active term is available only after the current term ends. {activeTermEndText}
        </Alert>
      )}
      {showEarlyHistoryInfo && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Term history is available after the current term ends. Completed term records will appear here once the term closes.
        </Alert>
      )}
      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {submissions.length === 0 ? (
        <Alert severity="info">No term history is available yet for your school work records.</Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Term</TableCell>
                <TableCell>Class</TableCell>
                <TableCell>Quiz Score</TableCell>
                <TableCell>Assignment Score</TableCell>
                <TableCell>Attendance %</TableCell>
                <TableCell>Eligible</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {submissions.map((record) => (
                <TableRow key={record._id} hover>
                  <TableCell>{record.termName || 'Unknown'}</TableCell>
                  <TableCell>{record.className || 'Unknown'}</TableCell>
                  <TableCell>{`${record.quizScoreObtained || 0} / ${record.quizScoreTotal || 0}`}</TableCell>
                  <TableCell>{`${record.assignmentScoreObtained || 0} / ${record.assignmentScoreTotal || 0}`}</TableCell>
                  <TableCell>{Number(record.attendancePercentage ?? 0).toFixed(2)}%</TableCell>
                  <TableCell>{record.isEligibleForExam ? 'Yes' : 'No'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default SchoolWorkTermHistory;
